import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const TutorialContext = createContext();

export const useTutorialContext = () => {
  const { t } = useTranslation();
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorialContext must be used within TutorialProvider');
  }
  return context;
};

// ─── Demo naming convention ────────────────────────────────────────────────────
export const DEMO_NAMES = {
  vehicle: 'DEMO-CAMION-01',
  vehicleMake: i18n.t('hardcoded.components_common_sidebar.arsenal'),
  vehicleModel: 'Demo Truck',
  vehicleType: 'truck',
  vehicleYear: 2026,
  receiver: 'DEMO-RECV-01',
  receiverMac: 'AA:BB:CC:DD:EE:01',
  sensors: ['DEMO-SEN-01', 'DEMO-SEN-02', 'DEMO-SEN-03', 'DEMO-SEN-04'],
  sensorCount: 4,
  profileName: 'DEMO-PERFIL-PRESION',
  profilePsiMin: 80,
  profilePsiMax: 120,
  profileTempMax: 70,
};

// ─── Step definitions ──────────────────────────────────────────────────────────
export const DEMO_STEPS = [
  {
    id: 'context',
    title: '1. Valida tu contexto',
    description: i18n.t('hardcoded.contexts_tutorialcontext.verifica_que_tienes_una_empresa'),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.revisa_en_la_barra_superior'),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.tu_empresa_activa_aparece_asociada'),
      i18n.t('hardcoded.contexts_tutorialcontext.si_no_tienes_empresa_contacta'),
      i18n.t('hardcoded.contexts_tutorialcontext.la_demo_creara_todos_los'),
    ],
    route: '/vehicles/schematics',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idcompany-context'),
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_contexto'),
    actionType: 'validate',
    autoAdvance: true,
    icon: 'business',
  },
  {
    id: 'vehicle',
    title: i18n.t('hardcoded.contexts_tutorialcontext.2_crea_el_vehiculo_demo'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.crea_un_vehiculo_llamado_vehicle', { vehicle: DEMO_NAMES.vehicle }),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_el_boton'),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.nombrealias_vehicle', { vehicle: DEMO_NAMES.vehicle }),
      i18n.t('hardcoded.contexts_tutorialcontext.marca_vehiclemake', { vehicleMake: DEMO_NAMES.vehicleMake }),
      i18n.t('hardcoded.contexts_tutorialcontext.modelo_vehiclemodel', { vehicleModel: DEMO_NAMES.vehicleModel }),
      i18n.t('hardcoded.contexts_tutorialcontext.tipo_vehicletype', { vehicleType: DEMO_NAMES.vehicleType }),
      i18n.t('hardcoded.contexts_tutorialcontext.ano_vehicleyear', { vehicleYear: DEMO_NAMES.vehicleYear }),
      i18n.t('hardcoded.contexts_tutorialcontext.selecciona_una_configuracion_de_ejes'),
      i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_guardar'),
    ],
    route: '/vehicles',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idadd-vehicle-btn'),
    secondaryTargetSelectors: [
      '[data-tutorial-id="vehicle-form-dialog"]',
      '[data-tutorial-id="vehicle-form-alias"]',
      '[data-tutorial-id="vehicle-form-type"]',
      '[data-tutorial-id="vehicle-form-year"]',
    ],
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_vehiculo'),
    actionType: 'validate',
    autoAdvance: false,
    icon: 'vehicle',
  },
  {
    id: 'receiver',
    title: i18n.t('hardcoded.contexts_tutorialcontext.3_registra_el_receptor_demo'),
    description: `Registra un receptor con MAC "${DEMO_NAMES.receiverMac}" y vincúlalo al vehículo demo.`,
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_registrar_receptor'),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.direccion_mac_receivermac', { receiverMac: DEMO_NAMES.receiverMac }),
      i18n.t('hardcoded.contexts_tutorialcontext.vehiculo_selecciona_vehicle', { vehicle: DEMO_NAMES.vehicle }),
      i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_guardar_para'),
      i18n.t('hardcoded.contexts_tutorialcontext.el_receptor_es_el_dispositivo'),
    ],
    route: '/receivers',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idadd-receiver-btn'),
    secondaryTargetSelectors: [
      '[data-tutorial-id="receiver-form-dialog"]',
      '[data-tutorial-id="receiver-form-mac"]',
      '[data-tutorial-id="receiver-form-vehicle"]',
    ],
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_receptor'),
    actionType: 'validate',
    autoAdvance: false,
    icon: 'receiver',
  },
  {
    id: 'sensors',
    title: i18n.t('hardcoded.contexts_tutorialcontext.4_registra_los_sensores_demo'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.registra_sensorcount_sensores_demo_namessenso', { sensorCount: DEMO_NAMES.sensorCount, DEMO_NAMESsenso: DEMO_NAMES.sensors.join(', ') }),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_registrar_sensor'),
    detailedHelp: [
      ...DEMO_NAMES.sensors.map((s, i) => `Sensor ${i + 1}: ID = "${s}"`),
      i18n.t('hardcoded.contexts_tutorialcontext.puedes_asignar_cada_sensor_al'),
      i18n.t('hardcoded.contexts_tutorialcontext.necesitas_crear_los_sensorcount_sensores', { sensorCount: DEMO_NAMES.sensorCount }),
      i18n.t('hardcoded.contexts_tutorialcontext.cada_sensor_monitorea_la_presion'),
    ],
    route: '/sensors',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idadd-sensor-btn'),
    secondaryTargetSelectors: [
      '[data-tutorial-id="sensor-form-dialog"]',
      '[data-tutorial-id="sensor-form-id"]',
      '[data-tutorial-id="sensor-form-vehicle"]',
      '[data-tutorial-id="sensor-form-position"]',
    ],
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_sensores'),
    actionType: 'validate',
    autoAdvance: false,
    icon: 'sensor',
    sensorsCreated: 0,
  },
  {
    id: 'assignments',
    title: i18n.t('hardcoded.contexts_tutorialcontext.5_asigna_sensores_y_receptor'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.abre_vehicle_y_asigna_los', { vehicle: DEMO_NAMES.vehicle }),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.ve_al_modulo_de_vehiculos', { vehicle: DEMO_NAMES.vehicle }),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_vehicle_para', { vehicle: DEMO_NAMES.vehicle }),
      i18n.t('hardcoded.contexts_tutorialcontext.ve_a_la_pestana_sensores'),
      i18n.t('hardcoded.contexts_tutorialcontext.asigna_demo_namessenso_posicion_1', { DEMO_NAMESsenso: DEMO_NAMES.sensors[0] }),
      i18n.t('hardcoded.contexts_tutorialcontext.asigna_demo_namessenso_posicion_2', { DEMO_NAMESsenso: DEMO_NAMES.sensors[1] }),
      i18n.t('hardcoded.contexts_tutorialcontext.asigna_demo_namessenso_posicion_3', { DEMO_NAMESsenso: DEMO_NAMES.sensors[2] }),
      i18n.t('hardcoded.contexts_tutorialcontext.asigna_demo_namessenso_posicion_4', { DEMO_NAMESsenso: DEMO_NAMES.sensors[3] }),
      i18n.t('hardcoded.contexts_tutorialcontext.el_receptor_ya_debe_estar'),
      i18n.t('hardcoded.contexts_tutorialcontext.guarda_los_cambios'),
    ],
    route: '/vehicles',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idadd-vehicle-btn'),
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_asignaciones'),
    actionType: 'validate',
    autoAdvance: false,
    icon: 'assignment',
  },
  {
    id: 'alarm_profile',
    title: i18n.t('hardcoded.contexts_tutorialcontext.6_configura_perfil_de_alarmas'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.crea_un_perfil_profilename_con', { profileName: DEMO_NAMES.profileName }),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_crear_perfil'),
    detailedHelp: [
      `Nombre: ${DEMO_NAMES.profileName}`,
      i18n.t('hardcoded.contexts_tutorialcontext.presion_minima_psi_profilepsimin', { profilePsiMin: DEMO_NAMES.profilePsiMin }),
      i18n.t('hardcoded.contexts_tutorialcontext.presion_maxima_psi_profilepsimax', { profilePsiMax: DEMO_NAMES.profilePsiMax }),
      i18n.t('hardcoded.contexts_tutorialcontext.temperatura_maxima_profiletempmaxc', { profileTempMax: DEMO_NAMES.profileTempMax }),
      i18n.t('hardcoded.contexts_tutorialcontext.activa_las_metricas_psi_y'),
      i18n.t('hardcoded.contexts_tutorialcontext.despues_de_crear_el_perfil', { vehicle: DEMO_NAMES.vehicle }),
      i18n.t('hardcoded.contexts_tutorialcontext.esto_define_cuando_el_sistema'),
    ],
    route: '/alarm-profiles',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idadd-profile-btn'),
    secondaryTargetSelectors: [
      '[data-tutorial-id="alarm-profile-form-dialog"]',
      '[data-tutorial-id="alarm-profile-name"]',
      '[data-tutorial-id="alarm-profile-psi-min"]',
      '[data-tutorial-id="alarm-profile-temp-max"]',
    ],
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_perfil'),
    actionType: 'validate',
    autoAdvance: false,
    icon: 'alarm',
  },
  {
    id: 'schematic',
    title: i18n.t('hardcoded.contexts_tutorialcontext.7_visualiza_el_esquematico'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.abre_el_panel_de_monitoreo'),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.navega_al_panel_de_esquematicos'),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.busca_vehicle_en_la_lista', { vehicle: DEMO_NAMES.vehicle }),
      i18n.t('hardcoded.contexts_tutorialcontext.veras_el_diagrama_del_camion'),
      i18n.t('hardcoded.contexts_tutorialcontext.cada_posicion_muestra_el_estado'),
      i18n.t('hardcoded.contexts_tutorialcontext.en_el_siguiente_paso_activaremos'),
      i18n.t('hardcoded.contexts_tutorialcontext.este_es_el_corazon_del'),
    ],
    route: '/vehicles/schematics',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idschematic-area'),
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_esquematico'),
    actionType: 'validate',
    autoAdvance: false,
    icon: 'schematic',
  },
  {
    id: 'simulate',
    title: i18n.t('hardcoded.contexts_tutorialcontext.8_activa_la_simulacion'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.activa_datos_simulados_de_presion'),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_iniciar_simulacion'),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.primero_se_generan_datos_normales'),
      i18n.t('hardcoded.contexts_tutorialcontext.luego_la_presion_baja_gradualmente'),
      i18n.t('hardcoded.contexts_tutorialcontext.cuando_cruza_el_limite_se'),
      i18n.t('hardcoded.contexts_tutorialcontext.se_crean_tickets_de_alarma'),
      i18n.t('hardcoded.contexts_tutorialcontext.se_envian_notificaciones_por_correo'),
      i18n.t('hardcoded.contexts_tutorialcontext.observa_el_esquematico_cambiar_de'),
    ],
    route: '/vehicles/schematics',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idschematic-area'),
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.iniciar_simulacion'),
    actionType: i18n.t('hardcoded.contexts_tutorialcontext.simulate'),
    autoAdvance: false,
    icon: 'simulate',
  },
  {
    id: 'tickets',
    title: i18n.t('hardcoded.contexts_tutorialcontext.9_revisa_tickets_y_notificaciones'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.revisa_los_tickets_de_alarma'),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.abre_el_modulo_de_tickets'),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.veras_tickets_con_estado_abierto'),
      i18n.t('hardcoded.contexts_tutorialcontext.cada_ticket_muestra_sensor_posicion'),
      i18n.t('hardcoded.contexts_tutorialcontext.haz_clic_en_un_ticket'),
      i18n.t('hardcoded.contexts_tutorialcontext.revisa_tu_correo_electronico_deberias'),
      i18n.t('hardcoded.contexts_tutorialcontext.si_ingresaste_tu_telefono_revisa'),
      i18n.t('hardcoded.contexts_tutorialcontext.cada_alarma_genera_1_ticket'),
    ],
    route: '/alarm-tickets',
    targetSelector: i18n.t('hardcoded.contexts_tutorialcontext.data-tutorial-idtickets-area'),
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.validar_tickets'),
    actionType: 'validate',
    autoAdvance: false,
    icon: 'ticket',
  },
  {
    id: 'complete',
    title: i18n.t('hardcoded.contexts_tutorialcontext.10_demo_completada'),
    description: i18n.t('hardcoded.contexts_tutorialcontext.felicidades_has_configurado_un_sistema'),
    instruction: i18n.t('hardcoded.contexts_tutorialcontext.has_completado_la_demo_guiada'),
    detailedHelp: [
      i18n.t('hardcoded.contexts_tutorialcontext.creaste_el_vehiculo_vehicle_con', { vehicle: DEMO_NAMES.vehicle }),
      i18n.t('hardcoded.contexts_tutorialcontext.registraste_el_receptor_receivermac_y', { receiverMac: DEMO_NAMES.receiverMac }),
      i18n.t('hardcoded.contexts_tutorialcontext.registraste_sensorcount_sensores_y_los', { sensorCount: DEMO_NAMES.sensorCount }),
      i18n.t('hardcoded.contexts_tutorialcontext.configuraste_el_perfil_profilename_con', { profileName: DEMO_NAMES.profileName }),
      i18n.t('hardcoded.contexts_tutorialcontext.visualizaste_el_esquematico_con_datos'),
      i18n.t('hardcoded.contexts_tutorialcontext.activaste_la_simulacion_y_viste'),
      i18n.t('hardcoded.contexts_tutorialcontext.revisaste_tickets_y_recibiste_notificaciones'),
      '',
      i18n.t('hardcoded.contexts_tutorialcontext.asi_funciona_arsenal_tpms_en'),
    ],
    route: null,
    targetSelector: null,
    actionLabel: i18n.t('hardcoded.contexts_tutorialcontext.finalizar_demo'),
    actionType: i18n.t('hardcoded.contexts_tutorialcontext.complete'),
    autoAdvance: false,
    icon: 'complete',
  },
];

const INITIAL_STATUS = DEMO_STEPS.reduce((acc, step) => {
  acc[step.id] = { status: 'pending', note: '' };
  return acc;
}, {});

export const TutorialProvider = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentCompany, user } = useAuth();

  const [isActive, setIsActive] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepStatus, setStepStatus] = useState(INITIAL_STATUS);
  const [demoContact, setDemoContact] = useState({ email: '', phone: '' });
  const [createdIds, setCreatedIds] = useState({});
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationPhase, setSimulationPhase] = useState('');
  const [showDemoModal, setShowDemoModal] = useState(false);
  const pollingRef = useRef(null);

  // ─── Auto-navigate when step changes ───────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    const step = DEMO_STEPS[currentStepIndex];
    if (step?.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, isActive]);

  // ─── Auto-validation polling ───────────────────────────────────────────────
  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    if (!isActive) return;
    const step = DEMO_STEPS[currentStepIndex];
    if (!step || step.actionType !== 'validate') return;

    // Auto-validate context step immediately
    if (step.id === 'context' && currentCompany?.company_id) {
      markStep(t('hardcoded.contexts_tutorialcontext.context'), t('profile.security.reset.steps.completed'), t('hardcoded.contexts_tutorialcontext.empresa_activa_currentcompanyc', { currentCompanyc: currentCompany.company_name || currentCompany.company_id }));
      setCreatedIds((prev) => ({ ...prev, company: currentCompany.company_id }));
    }

    // Poll for validation on create steps
    if ([t('hardcoded.components_ai_aientitycards.vehicle'), 'receiver', t('hardcoded.contexts_tutorialcontext.sensors'), 'assignments', 'alarm_profile', 'schematic', t('hardcoded.components_common_sidebar.tickets')].includes(step.id)) {
      pollingRef.current = setInterval(async () => {
        try {
          const result = await validateStep(step.id);
          if (result.ok) {
            markStep(step.id, t('profile.security.reset.steps.completed'), result.note);
          }
        } catch {
          // Silent polling failure
        }
      }, 4000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, isActive, currentCompany]);

  const currentStep = useMemo(() => DEMO_STEPS[currentStepIndex] || null, [currentStepIndex]);

  const markStep = useCallback((stepId, status, note = '') => {
    setStepStatus((prev) => ({ ...prev, [stepId]: { status, note } }));
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.min(prev + 1, DEMO_STEPS.length - 1));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const goToStep = useCallback((index) => {
    if (index >= 0 && index < DEMO_STEPS.length) {
      setCurrentStepIndex(index);
    }
  }, []);

  // ─── Validation logic ─────────────────────────────────────────────────────
  const validateStep = useCallback(async (stepId) => {
    if (stepId === 'context') {
      if (!currentCompany?.company_id) {
        return { ok: false, note: t('hardcoded.contexts_tutorialcontext.no_hay_empresa_activa_tu') };
      }
      setCreatedIds((prev) => ({ ...prev, company: currentCompany.company_id }));
      return { ok: true, note: t('hardcoded.contexts_tutorialcontext.empresa_currentcompanyc', { currentCompanyc: currentCompany.company_name || currentCompany.company_id }) };
    }

    if (stepId === 'vehicle') {
      try {
        const res = await api.vehicles.getAll();
        const vehicles = res?.data || [];
        const demo = vehicles.find((v) =>
          (v.alias || '').toUpperCase().includes('DEMO') ||
          (v.display_name || '').toUpperCase().includes('DEMO')
        );
        if (!demo) return { ok: false, note: t('hardcoded.contexts_tutorialcontext.crea_un_vehiculo_con_nombre', { vehicle: DEMO_NAMES.vehicle }) };
        setCreatedIds((prev) => ({ ...prev, vehicle: demo.id, vehicleName: demo.display_name || demo.alias }));
        return { ok: true, note: t('hardcoded.contexts_tutorialcontext.vehiculo_creado_demodisplay_nam', { demodisplay_nam: demo.display_name || demo.alias }) };
      } catch {
        return { ok: false, note: t('hardcoded.contexts_tutorialcontext.error_al_consultar_vehiculos') };
      }
    }

    if (stepId === 'receiver') {
      try {
        const res = await api.receivers.getAll();
        const receivers = res?.data || [];
        const demo = receivers.find((r) =>
          (r.mac_address || '').toUpperCase().includes('DEMO') ||
          (r.mac_address || '').includes('AA:BB:CC') ||
          (r.vehicle_id && String(r.vehicle_id) === String(createdIds.vehicle))
        );
        if (!demo) return { ok: false, note: t('hardcoded.contexts_tutorialcontext.registra_un_receptor_con_mac', { receiverMac: DEMO_NAMES.receiverMac, vehicle: DEMO_NAMES.vehicle }) };
        setCreatedIds((prev) => ({ ...prev, receiver: demo.id }));
        return { ok: true, note: t('hardcoded.contexts_tutorialcontext.receptor_registrado_mac_address', { mac_address: demo.mac_address }) };
      } catch {
        return { ok: false, note: t('hardcoded.contexts_tutorialcontext.error_al_consultar_receptores') };
      }
    }

    if (stepId === 'sensors') {
      try {
        const res = await api.sensors.getAll();
        const sensors = res?.data || [];
        const demoSensors = sensors.filter((s) =>
          (s.sensor_id || '').toUpperCase().includes('DEMO')
        );
        if (demoSensors.length < DEMO_NAMES.sensorCount) {
          const remaining = DEMO_NAMES.sensorCount - demoSensors.length;
          return {
            ok: false,
            note: `${demoSensors.length}/${DEMO_NAMES.sensorCount} sensores demo creados. Faltan ${remaining}. Siguiente: "${DEMO_NAMES.sensors[demoSensors.length]}"`,
          };
        }
        setCreatedIds((prev) => ({ ...prev, sensors: demoSensors.map((s) => s.id) }));
        return { ok: true, note: t('hardcoded.contexts_tutorialcontext.length_sensores_demo_registrados', { length: demoSensors.length }) };
      } catch {
        return { ok: false, note: t('hardcoded.contexts_tutorialcontext.error_al_consultar_sensores') };
      }
    }

    if (stepId === 'assignments') {
      try {
        const vehicleId = createdIds.vehicle;
        if (!vehicleId) return { ok: false, note: t('hardcoded.contexts_tutorialcontext.primero_necesitas_un_vehiculo_demo') };

        const [vehiclesRes, sensorsRes, receiversRes] = await Promise.all([
          api.vehicles.getAll(),
          api.sensors.getAll(),
          api.receivers.getAll(),
        ]);
        const vehicles = vehiclesRes?.data || [];
        const sensors = sensorsRes?.data || [];
        const receivers = receiversRes?.data || [];

        const demoVehicle = vehicles.find((v) => String(v.id) === String(vehicleId));
        if (!demoVehicle) return { ok: false, note: t('hardcoded.contexts_tutorialcontext.vehiculo_demo_no_encontrado') };

        const assignedSensors = sensors.filter((s) => s.vehicle_id && String(s.vehicle_id) === String(vehicleId));
        const assignedReceivers = receivers.filter((r) => r.vehicle_id && String(r.vehicle_id) === String(vehicleId));

        if (assignedSensors.length < DEMO_NAMES.sensorCount) {
          return {
            ok: false,
            note: `${assignedSensors.length}/${DEMO_NAMES.sensorCount} sensores asignados a "${DEMO_NAMES.vehicle}". Abre el vehículo y asigna los sensores.`,
          };
        }
        if (assignedReceivers.length < 1) {
          return { ok: false, note: t('hardcoded.contexts_tutorialcontext.falta_vincular_un_receptor_a', { vehicle: DEMO_NAMES.vehicle }) };
        }

        return { ok: true, note: t('hardcoded.contexts_tutorialcontext.length_sensores_length_receptor_asignados', { length: assignedSensors.length, length: assignedReceivers.length }) };
      } catch {
        return { ok: false, note: t('hardcoded.contexts_tutorialcontext.error_al_verificar_asignaciones') };
      }
    }

    if (stepId === 'alarm_profile') {
      try {
        const [profilesRes, assignmentsRes] = await Promise.all([
          api.alarmProfiles.getAll(),
          api.alarmProfiles.getAssignments({}),
        ]);
        const profiles = profilesRes?.data || [];
        const assignments = assignmentsRes?.data || [];

        if (!profiles.length) return { ok: false, note: t('hardcoded.contexts_tutorialcontext.crea_un_perfil_llamado_profilename', { profileName: DEMO_NAMES.profileName }) };

        const vehicleId = createdIds.vehicle;
        const vehicleAssigned = assignments.find((a) =>
          String(a.vehicle_id) === String(vehicleId)
        );

        if (!vehicleAssigned) {
          return { ok: false, note: t('hardcoded.contexts_tutorialcontext.perfil_creado_pero_no_asignado', { vehicle: DEMO_NAMES.vehicle }) };
        }

        setCreatedIds((prev) => ({ ...prev, alarm_profile: vehicleAssigned.profile_id || vehicleAssigned.profile?.id }));
        return { ok: true, note: t('hardcoded.contexts_tutorialcontext.perfil_configurado_y_asignado_al') };
      } catch {
        return { ok: false, note: t('hardcoded.contexts_tutorialcontext.error_al_verificar_perfiles') };
      }
    }

    if (stepId === 'schematic') {
      if (location.pathname.startsWith('/vehicles/schematics')) {
        return { ok: true, note: t('hardcoded.contexts_tutorialcontext.esquematico_visualizado_observa_las_posiciones') };
      }
      return { ok: false, note: t('hardcoded.contexts_tutorialcontext.navega_al_panel_de_esquematicos') };
    }

    if (stepId === 'tickets') {
      try {
        const res = await api.alarmTickets.getOpen();
        const tickets = res?.data || [];
        if (!tickets.length) {
          return { ok: false, note: t('hardcoded.contexts_tutorialcontext.aun_no_hay_tickets_asegurate') };
        }
        return { ok: true, note: t('hardcoded.contexts_tutorialcontext.length_tickets_de_alarma_generados', { length: tickets.length }) };
      } catch {
        return { ok: false, note: t('hardcoded.contexts_tutorialcontext.error_al_consultar_tickets') };
      }
    }

    if (stepId === 'complete') {
      return { ok: true, note: t('hardcoded.contexts_tutorialcontext.demo_completada_exitosamente') };
    }

    return { ok: false, note: t('hardcoded.contexts_tutorialcontext.paso_no_reconocido') };
  }, [currentCompany, createdIds.vehicle, location.pathname]);

  // ─── Execute validation for current step ────────────────────────────────────
  const validateCurrentStep = useCallback(async () => {
    if (!currentStep) return false;
    try {
      markStep(currentStep.id, 'running', t('hardcoded.components_auth_register.validando'));
      const result = await validateStep(currentStep.id);
      markStep(currentStep.id, result.ok ? t('profile.security.reset.steps.completed') : 'error', result.note || '');
      if (result.ok) {
        if (currentStepIndex < DEMO_STEPS.length - 1) {
          nextStep();
        } else {
          setIsCompleted(true);
          setIsActive(false);
        }
      }
      return result.ok;
    } catch (error) {
      markStep(currentStep.id, 'error', error?.response?.data?.detail || error?.message || t('hardcoded.contexts_tutorialcontext.error_al_validar'));
      return false;
    }
  }, [currentStep, currentStepIndex, markStep, nextStep, validateStep]);

  // ─── Simulation with phases ─────────────────────────────────────────────────
  const runSimulation = useCallback(async () => {
    try {
      setIsSimulating(true);
      markStep(t('hardcoded.contexts_tutorialcontext.simulate'), 'running', t('hardcoded.contexts_tutorialcontext.preparando_simulacion'));

      const payload = {
        admin_email: demoContact.email || user?.email || null,
        admin_phone: demoContact.phone || null,
        created_ids: createdIds,
      };

      // Phase 1: Activate simulation (baseline telemetry)
      setSimulationPhase(t('hardcoded.contexts_tutorialcontext.generando_datos_normales_de_presion'));
      const activate = await api.admin.runAlarmDemoStep('activate_simulation', payload);
      const activeIds = activate?.data?.created_ids || createdIds;
      setCreatedIds(activeIds);

      // Small delay so user can see normal state
      await new Promise((r) => setTimeout(r, 2000));

      // Phase 2: Trigger alarm events
      setSimulationPhase(t('hardcoded.contexts_tutorialcontext.simulando_caida_de_presion_y'));
      const trigger = await api.admin.runAlarmDemoStep('trigger_alarm_events', {
        ...payload,
        created_ids: activeIds,
      });
      const triggeredIds = trigger?.data?.created_ids || activeIds;
      setCreatedIds(triggeredIds);

      // Phase 3: Send notifications
      setSimulationPhase(t('hardcoded.contexts_tutorialcontext.enviando_notificaciones_correo_whatsapp_llamada'));
      await api.admin.runAlarmDemoStep('send_notifications', {
        ...payload,
        created_ids: triggeredIds,
      });

      setSimulationPhase('');
      markStep(t('hardcoded.contexts_tutorialcontext.simulate'), t('profile.security.reset.steps.completed'), t('hardcoded.contexts_tutorialcontext.simulacion_completada_se_generaron_alarmas'));
      setIsSimulating(false);
      nextStep();
      return true;
    } catch (error) {
      setSimulationPhase('');
      markStep(t('hardcoded.contexts_tutorialcontext.simulate'), 'error', error?.response?.data?.detail || error?.message || t('hardcoded.contexts_tutorialcontext.error_en_la_simulacion'));
      setIsSimulating(false);
      return false;
    }
  }, [createdIds, demoContact.email, demoContact.phone, markStep, nextStep, user?.email]);

  // ─── Execute current action ─────────────────────────────────────────────────
  const executeCurrentAction = useCallback(async () => {
    if (!currentStep) return;
    if (currentStep.actionType === 'validate') {
      await validateCurrentStep();
      return;
    }
    if (currentStep.actionType === 'simulate') {
      await runSimulation();
      return;
    }
    if (currentStep.actionType === 'complete') {
      markStep(t('hardcoded.contexts_tutorialcontext.complete'), t('profile.security.reset.steps.completed'), t('hardcoded.contexts_tutorialcontext.demo_completada'));
      setIsCompleted(true);
      setIsActive(false);
    }
  }, [currentStep, markStep, runSimulation, validateCurrentStep]);

  // ─── Tutorial lifecycle ─────────────────────────────────────────────────────
  const startTutorial = useCallback(() => {
    setIsActive(true);
    setIsCompleted(false);
    setCurrentStepIndex(0);
    setStepStatus(INITIAL_STATUS);
    setCreatedIds({});
    setIsSimulating(false);
    setSimulationPhase('');
  }, []);

  const skipTutorial = useCallback(() => {
    setIsActive(false);
    setIsSimulating(false);
    setSimulationPhase('');
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const completeTutorial = useCallback(() => {
    setIsActive(false);
    setIsCompleted(true);
    setIsSimulating(false);
    setSimulationPhase('');
  }, []);

  const resetTutorial = useCallback(() => {
    setIsActive(false);
    setIsCompleted(false);
    setCurrentStepIndex(0);
    setStepStatus(INITIAL_STATUS);
    setCreatedIds({});
    setIsSimulating(false);
    setSimulationPhase('');
  }, []);

  const value = {
    isActive,
    isCompleted,
    isSimulating,
    simulationPhase,
    currentStep,
    currentStepIndex,
    totalSteps: DEMO_STEPS.length,
    stepStatus,
    demoContact,
    createdIds,
    DEMO_STEPS,
    DEMO_NAMES,
    showDemoModal,
    setShowDemoModal,
    startTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial,
    resetTutorial,
    goToStep,
    validateCurrentStep,
    executeCurrentAction,
    setDemoContact,
  };

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};
